import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { platformAccessApi } from "../api/platformAccessApi";
import { TENANT_AI_WALLET_REFRESH_EVENT } from "../aiWalletRefresh";
import TenantAiWalletTopUpDialog from "./TenantAiWalletTopUpDialog";
import "./AiWalletStatusBar.css";

function compactMoney(microUsd) {
  const dollars = Number(microUsd || 0) / 1000000;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: dollars >= 1000 ? 0 : 2,
    notation: dollars >= 1000000 ? "compact" : "standard",
  }).format(dollars);
}

function legacyMoney(account, valueField, creditField) {
  if (Number.isFinite(Number(account?.[valueField]))) {
    return Number(account[valueField]);
  }
  return Number(account?.[creditField] || 0) *
    Number(account?.creditUnitMicroUsd || 1);
}

function authoritativeBalance(account) {
  if (account?.configured !== true) return null;
  if (Number.isFinite(account.balanceMicroUsd)) {
    return Number(account.balanceMicroUsd);
  }
  if (Number.isFinite(account.balanceCredits)) {
    return Number(account.balanceCredits) *
      Number(account.creditUnitMicroUsd || 1);
  }
  // Rolling deployments may still return the old summary without a settled
  // balance field. Only those legacy shapes fall back to available funds.
  const schemaVersion = Number(account.schemaVersion || 0);
  if (schemaVersion < 3 && Number.isFinite(account.availableBalanceMicroUsd)) {
    return Number(account.availableBalanceMicroUsd);
  }
  if (schemaVersion < 3 && Number.isFinite(account.availableCredits)) {
    return Number(account.availableCredits) *
      Number(account.creditUnitMicroUsd || 1);
  }
  return null;
}

function deductionMoney(microUsd) {
  const dollars = Number(microUsd || 0) / 1000000;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: dollars >= 1 ? 2 : dollars >= 0.01 ? 4 : 6,
  }).format(dollars);
}

export default function AiWalletStatusBar({
  canAddCredits = false,
  className = "",
  tenantId = "",
}) {
  const tenantScope = String(tenantId || "").trim();
  const [state, setState] = useState({
    data: null,
    error: false,
    loading: true,
    tenantScope,
  });
  const [deduction, setDeduction] = useState(null);
  const [addition, setAddition] = useState(null);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const previousBalanceRef = useRef(null);
  const accountVersionRef = useRef(null);
  const tenantIdRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const deductionSequenceRef = useRef(0);
  const additionSequenceRef = useRef(0);
  const tenantScopeRef = useRef(tenantScope);
  tenantScopeRef.current = tenantScope;

  useEffect(() => {
    if (!canAddCredits) setTopUpOpen(false);
  }, [canAddCredits]);

  const applyAuthoritativeSummary = useCallback((data, requestScope) => {
    if (requestScope !== tenantScopeRef.current) return false;
    const nextTenantId = String(data?.tenantId || "").trim() || null;
    const previousTenantId = tenantIdRef.current;
    const tenantChanged = previousTenantId !== null && nextTenantId !== null &&
      nextTenantId !== previousTenantId;
    if (tenantChanged) {
      previousBalanceRef.current = null;
      accountVersionRef.current = null;
      setDeduction(null);
      setAddition(null);
    }
    tenantIdRef.current = nextTenantId;

    const rawVersion = Number(data?.accountVersion);
    const nextVersion = Number.isSafeInteger(rawVersion) && rawVersion >= 0 ?
      rawVersion : null;
    const previousVersion = accountVersionRef.current;
    if (!tenantChanged && previousVersion !== null &&
      (nextVersion === null || nextVersion < previousVersion)) {
      return;
    }
    accountVersionRef.current = nextVersion;

    const nextBalance = authoritativeBalance(data);
    const previousBalance = previousBalanceRef.current;
    previousBalanceRef.current = nextBalance;
    if (previousBalance !== null && nextBalance !== null &&
      nextBalance < previousBalance) {
      setDeduction({
        amountMicroUsd: previousBalance - nextBalance,
        balanceMicroUsd: nextBalance,
        id: ++deductionSequenceRef.current,
      });
      setAddition(null);
    } else if (previousBalance !== null && nextBalance !== null &&
      nextBalance > previousBalance) {
      setAddition({
        amountMicroUsd: nextBalance - previousBalance,
        balanceMicroUsd: nextBalance,
        id: ++additionSequenceRef.current,
      });
      setDeduction(null);
    }
    setState({
      data,
      error: false,
      loading: false,
      tenantScope: requestScope,
    });
    return true;
  }, []);

  const load = useCallback(async () => {
    const requestSequence = ++requestSequenceRef.current;
    const requestScope = tenantScope;
    try {
      const data = await platformAccessApi.getTenantAiWalletSummary(
        requestScope,
      );
      if (requestSequence !== requestSequenceRef.current ||
          requestScope !== tenantScopeRef.current) return;
      applyAuthoritativeSummary(data, requestScope);
    } catch (_error) {
      if (requestSequence !== requestSequenceRef.current ||
          requestScope !== tenantScopeRef.current) return;
      setState({
        data: null,
        error: true,
        loading: false,
        tenantScope: requestScope,
      });
    }
  }, [applyAuthoritativeSummary, tenantScope]);

  useEffect(() => {
    previousBalanceRef.current = null;
    accountVersionRef.current = null;
    tenantIdRef.current = null;
    setDeduction(null);
    setTopUpOpen(false);
    setState({
      data: null,
      error: false,
      loading: true,
      tenantScope,
    });
    load();
    const loadWhenVisible = () => {
      if (typeof document === "undefined" ||
          document.visibilityState !== "hidden") {
        load();
      }
    };
    const interval = window.setInterval(loadWhenVisible, 60000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") load();
    };
    const handleWalletUpdate = (event) => {
      const wallet = event?.detail?.wallet;
      if (wallet && typeof wallet === "object" && !Array.isArray(wallet)) {
        const walletTenantId = String(wallet.tenantId || "").trim();
        if (tenantScope && walletTenantId !== tenantScope) {
          load();
          return;
        }
        // An embedded poll summary is newer than any outstanding standalone
        // read, so prevent a slower response from restoring stale UI state.
        requestSequenceRef.current += 1;
        applyAuthoritativeSummary(wallet, tenantScope);
        return;
      }
      load();
    };
    window.addEventListener(
      TENANT_AI_WALLET_REFRESH_EVENT,
      handleWalletUpdate,
    );
    window.addEventListener("tenant-ai-credits-updated", load);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      requestSequenceRef.current += 1;
      window.clearInterval(interval);
      window.removeEventListener(
        TENANT_AI_WALLET_REFRESH_EVENT,
        handleWalletUpdate,
      );
      window.removeEventListener("tenant-ai-credits-updated", load);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [applyAuthoritativeSummary, load, tenantScope]);

  useEffect(() => {
    if (!deduction) return undefined;
    const timeout = window.setTimeout(() => {
      setDeduction((current) => current?.id === deduction.id ? null : current);
    }, 2400);
    return () => window.clearTimeout(timeout);
  }, [deduction]);

  useEffect(() => {
    if (!addition) return undefined;
    const timeout = window.setTimeout(() => {
      setAddition((current) => current?.id === addition.id ? null : current);
    }, 2400);
    return () => window.clearTimeout(timeout);
  }, [addition]);

  const model = useMemo(() => {
    if (state.tenantScope !== tenantScope) {
      return { label: "AI Wallet", value: "Loading", percent: 18, tone: "loading" };
    }
    const data = state.data;
    if (state.loading) {
      return { label: "AI Wallet", value: "Loading", percent: 18, tone: "loading" };
    }
    if (state.error) {
      return { label: "AI Wallet", value: "Unavailable", percent: 0, tone: "muted" };
    }
    if (!data?.configured) {
      return { label: "AI Wallet", value: "Not funded", percent: 0, tone: "muted" };
    }
    const available = legacyMoney(
      data,
      "availableBalanceMicroUsd",
      "availableCredits",
    );
    const settledBalance = authoritativeBalance(data) ?? available;
    const billingStatus = String(data.billingStatus || "").toLowerCase();
    const billingReview = Number(data.billingHoldCount || 0) > 0 ||
      billingStatus === "review_required" ||
      billingStatus === "migration_review_required";
    const opening = legacyMoney(
      data,
      "periodOpeningBalanceMicroUsd",
      "periodOpeningCredits",
    );
    const funded = legacyMoney(
      data,
      "periodFundedMicroUsd",
      "periodTopUpsCredits",
    );
    const reserved = legacyMoney(data, "reservedMicroUsd", "reservedCredits");
    const spent = legacyMoney(
      data,
      "periodClientChargeMicroUsd",
      "periodConsumedCredits",
    );
    const capacity = Math.max(1, opening + funded, settledBalance);
    const percent = Math.max(
      0,
      Math.min(100, Math.round((settledBalance / capacity) * 100)),
    );
    const tone = settledBalance < 0 ? "overdrawn" :
      settledBalance === 0 ? "empty" : percent <= 15 ? "low" : "ready";
    return {
      label: "AI Wallet",
      value: compactMoney(settledBalance),
      percent,
      tone,
      title: settledBalance < 0 ?
        `${compactMoney(settledBalance)} balance. Overdrawn - top up to resume AI.` :
        `${compactMoney(settledBalance)} balance` +
          `${available !== settledBalance ?
            ` | ${compactMoney(available)} available` : ""}` +
          `${reserved ? ` | ${compactMoney(reserved)} reserved` : ""}` +
          ` | ${compactMoney(spent)} spent this period` +
          (billingReview ? " | Previous transactions are being reconciled; available funds can be used for new generation." : ""),
    };
  }, [state, tenantScope]);

  const statusContent = (
    <>
      <span className="ai-wallet-status__spark" aria-hidden="true">
        <span key={deduction?.id || addition?.id || "idle"}>&#10022;</span>
      </span>
      <span className="ai-wallet-status__copy">
        <small>{model.label}</small>
        <strong key={deduction?.id || addition?.id || model.value}>{model.value}</strong>
      </span>
      <span className="ai-wallet-status__track" aria-hidden="true">
        <i style={{ width: `${model.percent}%` }} />
      </span>
      {deduction ? (
        <span
          aria-hidden="true"
          className="ai-wallet-status__deduction"
          data-testid="ai-wallet-deduction"
          key={deduction.id}
        >
          <b>&minus;{deductionMoney(deduction.amountMicroUsd)}</b>
          <i /><i /><i />
        </span>
      ) : null}
      {addition ? (
        <span
          aria-hidden="true"
          className="ai-wallet-status__addition"
          data-testid="ai-wallet-addition"
          key={addition.id}
        >
          <b>+{deductionMoney(addition.amountMicroUsd)}</b>
          <i /><i /><i />
        </span>
      ) : null}
      <span
        aria-atomic="true"
        aria-live="polite"
        className="ai-wallet-status__announcement"
        role="status"
      >
        {deduction ?
          `AI Wallet reduced by ${deductionMoney(deduction.amountMicroUsd)}. ` +
            `New balance ${compactMoney(deduction.balanceMicroUsd)}.` :
          addition ?
            `AI Wallet increased by ${deductionMoney(addition.amountMicroUsd)}. ` +
              `New balance ${compactMoney(addition.balanceMicroUsd)}.` : ""}
      </span>
      {canAddCredits ? (
        <span aria-hidden="true" className="ai-wallet-status__action">
          + Add funds
        </span>
      ) : null}
    </>
  );
  const statusClass = `ai-wallet-status is-${model.tone}` +
    `${deduction ? " is-deducting" : ""}` +
    `${addition ? " is-crediting" : ""}` +
    `${canAddCredits ? " is-actionable" : ""}` +
    `${className ? ` ${className}` : ""}`;

  return (
    <>
      {canAddCredits ? (
        <button
          aria-label={`Add AI Wallet funds. ${model.title || model.value}`}
          className={statusClass}
          data-testid="ai-wallet-status"
          onClick={() => setTopUpOpen(true)}
          title={`${model.title || model.value}. Add funds.`}
          type="button"
        >
          {statusContent}
        </button>
      ) : (
        <div
          className={statusClass}
          data-testid="ai-wallet-status"
          title={model.title || model.value}
        >
          {statusContent}
        </div>
      )}
      <TenantAiWalletTopUpDialog
        key={tenantScope || "active-tenant-top-up"}
        onClose={() => setTopUpOpen(false)}
        open={topUpOpen}
      />
    </>
  );
}

export const _test = {
  authoritativeBalance,
  compactMoney,
  deductionMoney,
  legacyMoney,
};
